// S-04 Protection labels (Frontend §7.2; Security §3.6, §3.8, §11.3). UserFlows names the controls ("Booking cap",
// "Bot mode", thresholds, "Allowed embed origins") but not each threshold or decision: these are our copy.
import type { SecurityDecision } from '@pa/api/admin'

/** Security §3.6 limits, in the table's order, with what they count. */
export const LIMIT_LABELS: Record<string, string> = {
  visit_lookups: 'Lookups per visit',
  ip_lookups: 'Lookups per IP',
  ip_notfound: 'Not-found lookups per IP',
  name_dob_fails: 'Wrong dates of birth per name',
  clinic_lookups: 'Lookups per clinic',
  visits_ip: 'Visits started per IP',
  requests_visit: 'Requests per visit',
  hold_churn: 'Holds placed per visit',
  holds_placed_ip: 'Holds placed per IP',
  holds_active_ip: 'Active holds per IP',
  holds_active_clinic: 'Active holds per clinic',
  bookings_ip: 'Bookings per IP',
  challenges_shown: 'Challenges shown per visit',
  challenges_failed: 'Failed challenges per visit',
  challenges_failed_ip: 'Failed challenges per IP',
}

export const limitLabel = (code: string) => LIMIT_LABELS[code] ?? code

/** "per 10 min", "per 1 h"… from the limit's window (none: per visit, or at the same time). */
export function windowLabel(windowSec: number | undefined): string | null {
  if (windowSec === undefined) return null
  if (windowSec % 3_600 === 0) return `${String(windowSec / 3_600)} h`
  if (windowSec % 60 === 0) return `${String(windowSec / 60)} min`
  return `${String(windowSec)} s`
}

/** The decision column (UserFlows S-04 Protection: "would have" in Monitor mode). */
export const DECISION_LABELS: Record<SecurityDecision, string> = {
  flag: 'Flagged',
  would_challenge: 'Would have challenged',
  challenge: 'Challenged',
  challenge_passed: 'Challenge passed',
  challenge_failed: 'Challenge failed',
  block: 'Blocked',
  rate_limited: 'Rate limited',
  cap_reached: 'Booking cap reached',
  clinic_ceiling: 'Clinic lookup ceiling',
  recaptcha_unavailable: 'reCAPTCHA unavailable',
}

/** A block logged by a rule set to monitor (`ruleOverrides`) reads as "would have". */
export const decisionLabel = (decision: SecurityDecision, mode: 'monitor' | 'enforce') =>
  decision === 'block' && mode === 'monitor' ? 'Would have blocked' : DECISION_LABELS[decision]

/** The action column, from the row's route (`POST /v1/public/clinics/:clinicSlug/lookup` → "Lookup"). */
export function actionOfRoute(route: string): string {
  const path = route.split(' ')[1] ?? route
  if (path.endsWith('/lookup')) return 'Lookup'
  if (path.endsWith('/bookings')) return 'Booking'
  if (/\/holds(\/|$)/.test(path)) return 'Hold'
  if (path.endsWith('/sessions/current/end')) return 'Visit end'
  if (path.endsWith('/sessions')) return 'Visit start'
  if (path.endsWith('/availability')) return 'Availability'
  if (path.endsWith('/options')) return 'Options'
  if (path.endsWith('/events')) return 'Events'
  return path.split('/').pop() ?? route
}

export const MODE_LABELS = { monitor: 'Monitor', enforce: 'Enforce' } as const
